import React, { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import Avatar from "../components/ui/Avatar";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { useControlCash, CURRENCY_OPTIONS } from "../store/controlCashStore";
import { PASSWORD_RULES, validatePassword } from "../utils/passwordRules";

const UserSettings = () => {
  const { user, updateProfile, updatePassword, uploadAvatar, removeAvatar } = useAuth();
  const { state, dispatch } = useControlCash();
  const toast = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [name, setName] = useState(user?.name || "");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isSavingCurrencies, setIsSavingCurrencies] = useState(false);
  const enabledCurrencies = state.enabledCurrencies ?? ["PEN", "USD"];
  const [tempEnabledCurrencies, setTempEnabledCurrencies] = useState(enabledCurrencies);
  
  // Sincronizar el estado temporal cuando cambia el estado global
  React.useEffect(() => {
    console.log("🔄 UserSettings: enabledCurrencies cambió:", enabledCurrencies);
    setTempEnabledCurrencies(enabledCurrencies);
  }, [enabledCurrencies]);
  
  // Log cuando se monta el componente
  React.useEffect(() => {
    console.log("📱 UserSettings montado, enabledCurrencies inicial:", enabledCurrencies);
  }, []);

  const { results: passwordResults, allOk: passwordValid } = useMemo(
    () => validatePassword(newPassword),
    [newPassword]
  );

  React.useEffect(() => {
    if (user) setName(user.name || "");
  }, [user]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("El nombre no puede estar vacío");
      return;
    }
    setIsSavingProfile(true);
    const result = await updateProfile({ name: name.trim() });
    setIsSavingProfile(false);
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success("Perfil actualizado");
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten imágenes (JPG, PNG, WebP)");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen debe ser menor a 2 MB");
      return;
    }
    setIsUploadingAvatar(true);
    const result = await uploadAvatar(file);
    setIsUploadingAvatar(false);
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success("Foto actualizada");
    }
    e.target.value = "";
  };

  const handleRemoveAvatar = async () => {
    setIsUploadingAvatar(true);
    const result = await removeAvatar();
    setIsUploadingAvatar(false);
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success("Foto eliminada");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Completa todos los campos");
      return;
    }
    if (!passwordValid) {
      toast.error("La contraseña no cumple las reglas");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    const { supabase } = await import("../lib/supabase");
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword
    });
    if (signInError) {
      toast.error("La contraseña actual es incorrecta");
      return;
    }
    setIsSavingPassword(true);
    const result = await updatePassword(newPassword);
    setIsSavingPassword(false);
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success("Contraseña actualizada");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  const handleToggleCurrency = (code) => {
    const set = new Set(tempEnabledCurrencies);
    if (set.has(code)) {
      // Solo permitir eliminar si hay más de una moneda seleccionada
      if (set.size > 1) {
        set.delete(code);
      } else {
        toast.error("Debe haber al menos una moneda seleccionada");
        return;
      }
    } else {
      set.add(code);
    }
    setTempEnabledCurrencies(Array.from(set));
  };

  const hasCurrencyChanges = React.useMemo(() => {
    const tempSorted = [...tempEnabledCurrencies].sort((a, b) => a.localeCompare(b));
    const enabledSorted = [...enabledCurrencies].sort((a, b) => a.localeCompare(b));
    return JSON.stringify(tempSorted) !== JSON.stringify(enabledSorted);
  }, [tempEnabledCurrencies, enabledCurrencies]);

  const handleSaveCurrencies = async () => {
    if (tempEnabledCurrencies.length === 0) {
      toast.error("Debe haber al menos una moneda seleccionada");
      return;
    }
    
    // Verificar si hay cambios
    if (!hasCurrencyChanges) {
      toast.info("No hay cambios para guardar");
      return;
    }

    setIsSavingCurrencies(true);
    try {
      console.log("🔄 Guardando monedas habilitadas:", tempEnabledCurrencies);
      await dispatch({ type: "SET_ENABLED_CURRENCIES", payload: tempEnabledCurrencies });
      toast.success("Monedas actualizadas correctamente");
    } catch (err) {
      console.error("❌ Error guardando monedas:", err);
      
      // Mensaje de error más específico si falta la migración
      if (err.message && err.message.includes("enabled_currencies")) {
        toast.error(
          "Error: Falta ejecutar la migración SQL. Ve a Supabase Dashboard → SQL Editor y ejecuta: docs/supabase-migration-currency.sql",
          { duration: 8000 }
        );
      } else {
        toast.error("No se pudieron guardar las monedas. " + (err.message || ""));
      }
      
      // Revertir cambios en caso de error
      setTempEnabledCurrencies(enabledCurrencies);
    } finally {
      setIsSavingCurrencies(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-6 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver
        </button>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
          Configuración de usuario
        </h1>

        {/* Sección Avatar */}
        <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Foto de perfil
          </h2>
          <div className="flex items-center gap-6">
            <div className="relative">
              <Avatar
                src={user?.photoURL}
                name={user?.name}
                size="xl"
              />
              {isUploadingAvatar && (
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                  <svg className="animate-spin w-8 h-8 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleAvatarChange}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingAvatar}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors"
              >
                Cambiar foto
              </button>
              {user?.photoURL && (
                <button
                  onClick={handleRemoveAvatar}
                  disabled={isUploadingAvatar}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 text-sm font-medium rounded-xl transition-colors"
                >
                  Quitar foto
                </button>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
            Si no agregas una foto, se mostrará un avatar con tus iniciales.
          </p>
        </section>

        {/* Sección Datos personales */}
        <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Datos personales
          </h2>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div>
              <label htmlFor="settings-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nombre
              </label>
              <input
                id="settings-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tu nombre"
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                disabled={isSavingProfile}
              />
            </div>
            <div>
              <label htmlFor="settings-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <input
                id="settings-email"
                type="text"
                value={user?.email || ""}
                readOnly
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 rounded-xl border-2 border-gray-200 dark:border-gray-600 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                El email no se puede cambiar
              </p>
            </div>
            <button
              type="submit"
              disabled={isSavingProfile || name === (user?.name || "")}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors"
            >
              {isSavingProfile ? "Guardando..." : "Guardar cambios"}
            </button>
          </form>
        </section>

        {/* Sección Monedas habilitadas */}
        <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Monedas habilitadas
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Selecciona las monedas que quieres usar en tus transacciones. Estas aparecerán en los selectores de moneda al crear ingresos y gastos. Debes dejar al menos una habilitada.
          </p>
          <div className="flex flex-wrap gap-3 mb-4">
            {CURRENCY_OPTIONS.map((opt) => (
              <label
                key={opt.code}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-colors cursor-pointer ${
                  tempEnabledCurrencies.includes(opt.code)
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-600"
                    : "border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                <input
                  type="checkbox"
                  checked={tempEnabledCurrencies.includes(opt.code)}
                  onChange={() => handleToggleCurrency(opt.code)}
                  disabled={isSavingCurrencies}
                  className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <span className={`text-sm font-medium ${
                  tempEnabledCurrencies.includes(opt.code)
                    ? "text-blue-700 dark:text-blue-300"
                    : "text-gray-700 dark:text-gray-200"
                }`}>
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
          
          {/* Botón Guardar */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex-1">
              {tempEnabledCurrencies.length === 1 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Debe haber al menos una moneda seleccionada.
                </p>
              )}
              {hasCurrencyChanges && (
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Tienes cambios sin guardar
                </p>
              )}
            </div>
            <button
              onClick={handleSaveCurrencies}
              disabled={isSavingCurrencies || !hasCurrencyChanges || tempEnabledCurrencies.length === 0}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors flex items-center gap-2"
            >
              {isSavingCurrencies ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Guardando...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Guardar cambios
                </>
              )}
            </button>
          </div>
        </section>

        {/* Sección Cambiar contraseña */}
        <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Cambiar contraseña
          </h2>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Contraseña actual
              </label>
              <div className="relative">
                <input
                  id="current-password"
                  type={showPasswords ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Tu contraseña actual"
                  className="w-full px-4 py-3 pr-12 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:outline-none focus:border-blue-500"
                  autoComplete="current-password"
                  disabled={isSavingPassword}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  aria-label={showPasswords ? "Ocultar" : "Mostrar"}
                >
                  {showPasswords ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nueva contraseña
              </label>
              <input
                id="new-password"
                type={showPasswords ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Ej: MiPass123!"
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:outline-none focus:border-blue-500"
                autoComplete="new-password"
                disabled={isSavingPassword}
              />
              <div className="mt-2 space-y-1 text-sm">
                {PASSWORD_RULES.map((rule) => (
                  <div
                    key={rule.id}
                    className={`flex items-center gap-2 ${passwordResults.find((r) => r.id === rule.id)?.ok ? "text-green-600 dark:text-green-400" : "text-gray-500 dark:text-gray-400"}`}
                  >
                    {passwordResults.find((r) => r.id === rule.id)?.ok ? "✓" : "○"} {rule.label}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirmar nueva contraseña
              </label>
              <input
                id="confirm-password"
                type={showPasswords ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la contraseña"
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:outline-none focus:border-blue-500"
                autoComplete="new-password"
                disabled={isSavingPassword}
              />
            </div>
            <button
              type="submit"
              disabled={isSavingPassword || !currentPassword || !passwordValid || newPassword !== confirmPassword}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors"
            >
              {isSavingPassword ? "Actualizando..." : "Cambiar contraseña"}
            </button>
          </form>
        </section>
      </div>
    </Layout>
  );
};

export default UserSettings;
